import type {TFunction} from "i18next";

import type {CitationTypeEmbedded} from "../types";

/**
 * Citation type labels resolve to `citation.type.{catalogKey}` in the locale catalog.
 *
 * **Catalog key order (matches Manager / API):**
 * 1. `key` from DB (`citation_types.key`) — e.g. `epxpired_registration`, `over_maximun_permits`
 * 2. `type` field when `key` is absent
 * 3. Slugified English `name` / `description` — fallback only; may differ from DB key
 *
 * Manager mirrors API keys in catalog; v2.3.1+ adds aliases where slug fallback would miss
 * (e.g. `citation.type.expired_registration` alongside `citation.type.epxpired_registration`).
 * Long-term typo fixes belong in API + data migration, not catalog-only renames.
 */

/** Slug English citation type label → name-based fallback key (not preferred over API `key`). */
function citationTypeNameToCatalogSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/_ _ _/g, "xx")
    .replace(/'s\b/g, "s")
    .replace(/'/g, "")
    .replace(/\(s\)/g, "_s")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function citationTypeCatalogSlug(ct: CitationTypeEmbedded): string | null {
  const fromKey = ct.key?.trim().toLowerCase();
  if (fromKey) return fromKey.replace(/-/g, "_");

  const fromType = ct.type?.trim().toLowerCase();
  if (fromType) return fromType.replace(/-/g, "_");

  const fallback = ct.name?.trim() || ct.description?.trim();
  if (!fallback) return null;

  const slug = citationTypeNameToCatalogSlug(fallback);
  return slug || null;
}

export function tCitationTypeLabel(
  ct: CitationTypeEmbedded,
  tCitation: TFunction<"citation">,
): string | null {
  const englishFallback =
    ct.name?.trim() || ct.description?.trim() || ct.type?.trim() || null;
  if (!englishFallback) return null;

  const slug = citationTypeCatalogSlug(ct);
  if (!slug) return englishFallback;

  return tCitation(`type.${slug}`, {defaultValue: englishFallback});
}
